import React, { useEffect, useState } from "react";
import { CheckCircle, ShoppingBag, Package, Briefcase } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useTranslations } from "@/shared/hooks";
import { useOrder, useAuth, useOrgaConfigurationContext } from "@/app/providers";
import { useNavigation } from "@/shared/hooks/useNavigation";
import { useOrderJourneySecurity } from "@/shared/hooks/useOrderJourneySecurity";
import { useOrderPersonalization } from "@/shared/hooks/useOrderPersonalization";
import SeoTags from '@/components/shared/SeoTags';

const OrderConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const { idorga } = useParams<{ idorga: string }>();
  const { navigateToHome, navigateToOrder, navigateToCustomerOrders } = useNavigation();
  const { t } = useTranslations();
  const { i18n } = useTranslation();
  const { currentUser } = useAuth();
  const {
    orderData,
    isOrderComplete,
    clearOrder,
    currentOrderId,
  } = useOrder();

  const { configuration: orgaConfig } = useOrgaConfigurationContext();
  const personalization = useOrderPersonalization();

  const handleMyOrders = () => {
    navigateToCustomerOrders();
  };

  const handleNewOrder = () => {
    clearOrder();
    navigateToOrder();
  };

  // Force la couleur du texte des boutons primaires
  useEffect(() => {
    const primaryButtons = document.querySelectorAll('button[data-theme-button="primary"]');
    primaryButtons.forEach((button) => {
      const htmlButton = button as HTMLElement;
      htmlButton.style.setProperty('color', personalization.button.primaryText || '#FFFFFF', 'important');
    });
  }, [personalization.button.primaryText, personalization.button.primary]);

  const getBackgroundStyle = (): React.CSSProperties => {
    const bgType = personalization.layout.backgroundType;
    const bgValue = personalization.layout.background || '#FFFFFF';
    if (bgType === 'image') {
      return {
        backgroundImage: bgValue.startsWith('url(') ? bgValue : `url(${bgValue})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    } else if (bgType === 'gradient') {
      return { background: bgValue };
    } else {
      return { backgroundColor: bgValue };
    }
  };

  const getCardStyles = () => {
    const style: React.CSSProperties = {
      backgroundColor: personalization.card.background || '#FFFFFF',
      borderColor: personalization.card.border || '#E5E7EB',
      borderWidth: '1px',
      borderStyle: 'solid',
    };
    const classes: string[] = [personalization.card.borderRadius || 'rounded-3xl', personalization.card.shadow || 'shadow-lg'];
    return { style, className: classes.join(' ') };
  };

  const getButtonStyles = (variant: 'primary' | 'secondary' = 'primary') => {
    const style: React.CSSProperties = {
      borderRadius: personalization.button.borderRadius === 'rounded-full' ? '9999px' : '1rem',
    };
    const classes: string[] = ['font-bold', 'transition-all', 'px-4', 'py-2.5', 'text-sm', 'flex', 'items-center', 'justify-center', 'shadow-md', 'hover:shadow-lg', 'active:scale-95'];

    if (variant === 'primary') {
      if (personalization.button.primary) {
        if (personalization.button.primary.includes('gradient')) {
          style.background = personalization.button.primary;
        } else {
          style.backgroundColor = personalization.button.primary;
        }
      }
      style.color = personalization.button.primaryText || '#FFFFFF';
    } else {
      style.backgroundColor = personalization.card.background || '#FFFFFF';
      style.borderColor = personalization.button.primary;
      style.borderWidth = '2px';
      style.borderStyle = 'solid';
      style.color = personalization.button.primary;
    }

    return { style, className: classes.join(' ') };
  };

  const { hasOrganizationChanged } = useOrderJourneySecurity();

  useEffect(() => {
    if (!isOrderComplete) {
      navigateToHome();
    }
  }, [isOrderComplete, navigateToHome]);

  if (hasOrganizationChanged || !isOrderComplete) return null;

  const orderNumber = currentOrderId || `LS-${Math.floor(Math.random() * 100000)}`;
  const recruitmentEnabled = orgaConfig?.recruitment?.enabled;
  const currentLang = i18n.language.split('-')[0] as 'fr' | 'en' | 'es';
  const recruitmentDescription = orgaConfig?.recruitment?.description?.[currentLang] || orgaConfig?.recruitment?.description?.['fr'];

  // Détection de couleur claire pour le contraste
  const isColorLight = (color?: string) => {
    if (!color || color.includes('gradient')) return false;
    const hex = color.replace('#', '');
    if (hex.length !== 6) return false;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 180;
  };

  return (
    <div
      className="animate-fade-in min-h-screen flex flex-col items-center justify-center p-4 py-4"
      style={getBackgroundStyle()}
    >
      <SeoTags
        title={`Commande confirmée | ${orgaConfig?.name || 'CashLive'}`}
        description={`Merci pour votre achat`}
        url={typeof window !== 'undefined' ? window.location.href : ''}
        noIndex={true}
      />

      <div className="w-full max-w-lg mx-auto space-y-4">
        {/* RECRUITMENT SECTION - Correction des couleurs pour utiliser Button Primary */}
        {recruitmentEnabled && recruitmentDescription && (() => {
          const isLight = isColorLight(personalization.button.primary);
          const bg = personalization.button.primary || '#9333EA';
          const textCol = isLight ? '#111827' : '#FFFFFF';
          const subTextCol = isLight ? '#374151' : 'rgba(255, 255, 255, 0.9)';

          return (
            <div
              className="relative overflow-hidden rounded-xl p-3 shadow-xl border-t-4"
              style={{
                background: bg.includes('gradient') ? bg : `linear-gradient(135deg, ${bg}, ${bg}e6)`,
                borderTopColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.3)',
                boxShadow: `0 20px 25px -5px ${bg}40`
              }}
            >
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light"></div>
              <div className="relative flex flex-col sm:flex-row items-center gap-2">
                <div className="p-1.5 bg-white/20 backdrop-blur-md rounded-lg border border-white/40 shadow-lg shrink-0">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-sm font-black mb-0.5 flex items-center justify-center sm:justify-start gap-1.5" style={{ color: textCol }}>
                    {orgaConfig?.name ? `${t('recruitment.join', 'Rejoignez')} ${orgaConfig.name} !` : t('recruitment.title')}
                    <span className="h-1.5 w-1.5 bg-yellow-400 rounded-full animate-ping"></span>
                  </h3>
                  <p className="text-xs font-bold leading-tight" style={{ color: subTextCol }}>
                    {recruitmentDescription}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* MAIN SUCCESS CARD */}
        <div
          className={`p-4 sm:p-5 text-center ${getCardStyles().className}`}
          style={getCardStyles().style}
        >
          <div className="flex justify-center mb-3">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-inner animate-bounce-slow"
              style={{ backgroundColor: `${personalization.icon.primary}15` }}
            >
              <CheckCircle className="w-9 h-9" style={{ color: personalization.icon.primary }} />
            </div>
          </div>

          <h1 className="text-2xl font-black mb-2 tracking-tight" style={{ color: personalization.text.heading }}>
            {t("confirmation.thankYou")}
          </h1>

          <p className="text-sm mb-4 font-medium opacity-80" style={{ color: personalization.text.secondary }}>
            {t('confirmation.successDescription')}
          </p>

          {/* ORDER DETAILS - Utilise Badge Colors */}
          <div className="rounded-2xl p-4 mb-4 border-2 shadow-inner" style={{ backgroundColor: personalization.badge.background, borderColor: personalization.card.border }}>
            <p className="text-[9px] font-black uppercase tracking-[0.2em] mb-1 opacity-60" style={{ color: personalization.text.secondary }}>
              {t('confirmation.orderNumberLabel')}
            </p>
            <p className="text-lg font-black font-mono tracking-tighter break-all" style={{ color: personalization.badge.text }}>
              {orderNumber}
            </p>
          </div>

          {/* AUTH / STATUS CARD */}
          {currentUser ? (
            <div className="rounded-xl p-3 mb-4 flex items-center justify-center gap-2 border-2 shadow-sm" style={{ backgroundColor: `${personalization.button.primary}05`, borderColor: `${personalization.button.primary}20` }}>
              <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: personalization.button.primary }}>
                <CheckCircle className="w-3 h-3 text-white" />
              </div>
              <p className="font-bold text-sm" style={{ color: personalization.text.primary }}>
                {t('confirmation.loggedIn.message', { email: currentUser.email })}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl p-4 mb-4 text-left border-2 shadow-md" style={{ backgroundColor: personalization.badge.background, borderColor: personalization.card.border }}>
              <h3 className="text-base font-black mb-1" style={{ color: personalization.text.heading }}>🚀 {t('confirmation.authPromo.title')}</h3>
              <p className="text-sm mb-3 font-medium opacity-80" style={{ color: personalization.text.secondary }}>{t('confirmation.authPromo.description', { email: orderData.email })}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  data-theme-button="primary"
                  onClick={() => navigate('/auth/login')}
                  style={getButtonStyles('primary').style}
                  className={getButtonStyles('primary').className + " flex-1"}
                >
                  {t('header.signIn')}
                </button>
                <button
                  onClick={() => navigate('/auth/register')}
                  style={getButtonStyles('secondary').style}
                  className={getButtonStyles('secondary').className + " flex-1"}
                >
                  {t('header.register')}
                </button>
              </div>
            </div>
          )}

          {/* ACTION BUTTONS - MES COMMANDES / NOUVELLE COMMANDE */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t-2" style={{ borderColor: personalization.card.border }}>
            {currentUser && (
              <button
                data-theme-button="primary"
                onClick={handleMyOrders}
                style={getButtonStyles('primary').style}
                className={getButtonStyles('primary').className}
              >
                <Package className="w-4 h-4 mr-2" />
                {t('common.myOrders', 'Mes commandes')}
              </button>
            )}
            <button
              onClick={handleNewOrder}
              style={currentUser ? getButtonStyles('secondary').style : getButtonStyles('primary').style}
              className={currentUser ? getButtonStyles('secondary').className : getButtonStyles('primary').className}
              data-theme-button={currentUser ? "secondary" : "primary"}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {t('common.newOrder', 'Nouvelle commande')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
